import * as React from 'react';

/**
 * A hook to detect if a scrollable element has scrolled to the top or bottom
 * and shows shadows accordingly.
 *
 * @param ref - React ref to the scrollable element
 * @returns Object containing showTopShadow and showBottomShadow states
 */
export const useScrollVShadow = <T extends HTMLElement>(
  ref: React.RefObject<T | null>,
) => {
  const [showTopShadow, setShowTopShadow] = React.useState(false);
  const [showBottomShadow, setShowBottomShadow] = React.useState(false);

  React.useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const checkScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = element;
      // Show top shadow if scrolled down
      setShowTopShadow(scrollTop > 0);
      // Show bottom shadow if not at the bottom
      setShowBottomShadow(scrollTop + clientHeight < scrollHeight - 1);
    };

    // Check initial state
    checkScroll();

    // Add scroll event listener
    element.addEventListener('scroll', checkScroll);

    // Clean up
    return () => {
      element.removeEventListener('scroll', checkScroll);
    };
  }, [ref]);

  return { showTopShadow, showBottomShadow };
};

export default useScrollVShadow;
